import { colors } from "@elit-hair/elit-tools";
import { GlobalStyles, LinkProps } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
    arSA as coreArSa,
    deDE as coreDeDE,
    enUS as coreEnUS,
    esES as coreEsES,
    trTR as coreTrTR,
} from "@mui/material/locale";
import { ThemeOptions, ThemeProvider, createTheme } from "@mui/material/styles";
import {
    arSD as DGarSa,
    deDE as DGdeDE,
    enUS as DGenUS,
    esES as DGesES,
    trTR as DGtrTR,
} from "@mui/x-data-grid/locales";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Settings } from "luxon";
import React, { ReactElement, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router-dom";

Settings.defaultZone = "Europe/Istanbul";

const getCoreLocale = (locale: string) => {
    if (locale === "ar") return coreArSa;
    if (locale === "de") return coreDeDE;
    if (locale === "es") return coreEsES;
    if (locale === "tr") return coreTrTR;
    return coreEnUS;
};
const getDataGridLocale = (locale: string) => {
    if (locale === "ar") return DGarSa;
    if (locale === "de") return DGdeDE;
    if (locale === "es") return DGesES;
    if (locale === "tr") return DGtrTR;
    return DGenUS;
};

export const palette = {
    primary: {
        main: colors.elitBlue,
    },
    secondary: {
        main: grey[600],
    },
    error: {
        main: colors.elitRed,
    },
    info: {
        main: colors.elitButtonDisabled,
    },
    success: {
        main: "#008000",
    },
};

const LinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }>(
    (props, ref) => {
        const { href, ...other } = props;
        // Map href (Material UI) -> to (react-router)
        return <RouterLink ref={ref} to={href} {...other} />;
    }
);

// We define 2 themes, both of them are being used in the app
export const lightTheme: ThemeOptions = {
    typography: {
        fontFamily: "Geist, Roboto, Helvetica, Arial, sans-serif",
    },
    palette,
    components: {
        MuiLink: {
            defaultProps: {
                component: LinkBehavior,
            } as LinkProps,
        },
        MuiButtonBase: {
            defaultProps: {
                LinkComponent: LinkBehavior,
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    // borderRadius: "1rem",
                    fontWeight: 500,
                    textTransform: "capitalize",
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        // borderRadius: "1rem",
                    },
                    "& .MuiOutlinedInput-input": {
                        paddingTop: "6.75px",
                        paddingBottom: "6.75px",
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    border: "1px solid grey",
                    boxShadow:
                        "rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px",
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    // borderRadius: "1rem",
                },
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    "& .MuiDataGrid-container--top [role=row]": {
                        background: colors.elitLightBackground,
                        ["& .MuiDataGrid-columnHeader"]: {
                            background: colors.elitLightBackground,
                        },
                    },
                    "& .MuiDataGrid-overlay": {
                        background: "transparent",
                    },
                },
            },
        },
    },
};

export const darkTheme = {
    ...lightTheme,
    components: {
        MuiLink: {
            defaultProps: {
                component: LinkBehavior,
            } as LinkProps,
        },
        MuiButtonBase: {
            defaultProps: {
                LinkComponent: LinkBehavior,
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    // borderRadius: "1rem",
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        fontSize: "12px",
                        // borderRadius: "2rem",
                        backgroundColor: "white",
                        paddingLeft: "19px",
                        "& input": {
                            padding: "10px",
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    // borderRadius: "1rem",
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    "& .MuiInputBase-root.MuiOutlinedInput-root": {
                        paddingLeft: "14px",
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                    },
                },
            },
        },
    },
};

// getCoreLocale(i18next.language), // core translations by MUI
// getDataGridLocale(i18next.language) // core translations by MUI

const MUIThemeProvider = ({ children }: { children: ReactElement }) => {
    const { i18n } = useTranslation();

    // HACK: To hide text from MUI for DEVELOPMENT
    useEffect(() => {
        const removeMuiText = () => {
            setTimeout(() => {
                document.querySelectorAll("div").forEach((div) => {
                    if (div.textContent?.trim() === "MUI X Missing license key") {
                        div.style.display = "none";
                    }
                });
            }, 100);
        };
        const timer = setInterval(() => {
            removeMuiText();
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const themeWithLocale = useMemo(
        () => createTheme(lightTheme, getCoreLocale(i18n.language), getDataGridLocale(i18n.language)),
        [i18n.language, lightTheme]
    );

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <GlobalStyles
                styles={{
                    "& .MuiDataGrid-virtualScroller ": {
                        // overflow: "scroll !important",
                    },
                }}
            />
            <ThemeProvider theme={themeWithLocale}>{children}</ThemeProvider>
        </LocalizationProvider>
    );
};

export default MUIThemeProvider;
